import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { ICandidatDocumentModel } from "@common-models/*"
import { toast } from "react-toastify"
import { getDocos, uploadDoco } from "../../../../core/_requests"
import { useSearchResultsContext } from "../../CandidatsRechercheResults"
import { useSearchResultsPreviewContext } from "../_CandidatsRechercheResultsPreview"
import MyAlert from "@common-utils/MyAlert"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import MyTooltip from "@common-utils/MyTooltip"
import { ISearchResultsPreviewDocoListingContextPropsModel } from "../../core/_models"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MyLoadingOverlay from "../../../../../../../../common/components/MyLoadingOverlay"
import MyModal from "@common-utils/MyModal"
import useSwal from "@common-hooks/useSwal"
import { deleteDoco, lockDoco, unlockDoco } from "../../../../doco/core/_requests"
import CandidatDocoPartagerParMailForm from "../../../../doco/_CandidatDocoPartagerParMailForm"
import CandidatGenererDocoEtNotifierCandidatParMailForm from "../../../../actions/_CandidatGenererDocoEtNotifierCandidatParMailForm"
import clsx from "clsx"
import MyGoogleDriveFileViewerInModal from "../../../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { CONST_MIME_APPLICATION_GOOGLE_DOC } from "@common-constants/*"

const REACT_QUERY_KEY_GET_DOCOS_QUERY = "REACT_QUERY_KEY_GET_DOCOS_QUERY"

// --- DOCO ITEM COMPONENT ---
const DocoItem = ({ item, index, candidatId }: { item: ICandidatDocumentModel; index: number; candidatId: number }) => {
     const { swal } = useSwal()
     const docoListingContext = useDocoListingContext()
     const queryClient = useQueryClient()
     const [isLocking, setIsLocking] = useState(false)

     function deleteItem() {
          return swal.fire({
               icon: "warning",
               title: `Suppression du DoCo`,
               text: `Voulez-vous vraiment supprimer "${item.intitule ? item.intitule : `DoCo n°${index + 1}`}" ?`,
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    return deleteDoco(item.id as number)
                         .then(() => {
                              queryClient.setQueryData<ICandidatDocumentModel[] | undefined>([REACT_QUERY_KEY_GET_DOCOS_QUERY, candidatId], prev =>
                                   prev ? prev.filter(item_ => item_.id !== item.id) : prev
                              )
                              toast.success("Document supprimé avec succès.")
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail || "Erreur lors de la suppression")
                         })
               },
          })
     }

     function toggleLock() {
          if (isLocking) return
          setIsLocking(true)
          const action = item.isLocked ? unlockDoco : lockDoco
          const successMsg = item.isLocked ? "déverrouillé" : "verrouillé"

          action(item.id as number)
               .then(() => {
                    queryClient.setQueryData<ICandidatDocumentModel[] | undefined>([REACT_QUERY_KEY_GET_DOCOS_QUERY, candidatId], prev => {
                         if (prev) {
                              const idx = prev.findIndex(i => i.id === item.id)
                              if (idx > -1) prev[idx] = { ...prev[idx], isLocked: !item.isLocked }
                              return [...prev]
                         }
                         return prev
                    })
                    toast.success(`Le DoCo a été ${successMsg}.`)
               })
               .catch(e => toast.error(e?.response?.data?.detail))
               .finally(() => setIsLocking(false))
     }

     return (
          <div className="card h-100 border border-dashed border-gray-300 hover-elevate-up">
               <div className="card-body d-flex justify-content-center text-center flex-column p-4">
                    <div
                         className="text-gray-800 d-flex flex-column cursor-pointer align-items-center"
                         onClick={() => docoListingContext.setSelectedDocoInPreview({ item: item, mode: "view", loading: true })}
                    >
                         <div className="symbol symbol-60px mb-4 position-relative">
                              {item.isLocked && (
                                   <span className="position-absolute top-0 start-100 translate-middle badge badge-circle badge-warning">
                                        <i className="fas fa-lock text-white fs-9"></i>
                                   </span>
                              )}
                              <img src={toAbsoluteUrl("/media/svg/files/doc.svg")} alt="" />
                         </div>
                         <div className="fs-6 fw-bolder mb-1 text-truncate w-100 text-hover-primary" title={item.intitule || ""}>
                              {item.intitule ? item.intitule : `DoCo n°${index + 1}`}
                         </div>
                         <div className="fs-7 text-muted mb-4">
                              <div className="mb-1">{item.createdAt?.format("DD/MM/YYYY HH:mm")}</div>
                         </div>
                    </div>

                    <div className="d-flex justify-content-center gap-2 mb-4">
                         <MyTooltip title="Voir / Editer">
                              <button
                                   className="btn btn-sm btn-icon btn-light-success"
                                   onClick={() => docoListingContext.setSelectedDocoInPreview({ item, mode: "view", loading: true })}
                              >
                                   <i className="fas fa-eye fs-5"></i>
                              </button>
                         </MyTooltip>
                         <MyTooltip title="Partager par mail">
                              <button className="btn btn-sm btn-icon btn-light-primary" onClick={() => docoListingContext.setDocoPartagerParMail(item)}>
                                   <i className="fas fa-share fs-5"></i>
                              </button>
                         </MyTooltip>
                         <MyTooltip title="Télécharger PDF">
                              <a href={FileAndDriveHelper.getUrlExportToPdfFormat(item.googleDriveID as string)} target="_blank" className="btn btn-sm btn-icon btn-light-dark">
                                   <i className="fas fa-file-pdf fs-5"></i>
                              </a>
                         </MyTooltip>
                         <MyTooltip title={item.isLocked ? "Déverrouiller" : "Verrouiller"}>
                              <button className={`btn btn-sm btn-icon ${item.isLocked ? "btn-warning" : "btn-light-warning"}`} onClick={toggleLock} disabled={isLocking}>
                                   {isLocking ? (
                                        <span className="spinner-border spinner-border-sm"></span>
                                   ) : (
                                        <i className={`fas fa-${item.isLocked ? "lock" : "lock-open"} fs-5`}></i>
                                   )}
                              </button>
                         </MyTooltip>
                         <MyTooltip title="Supprimer">
                              <button className="btn btn-sm btn-icon btn-light-danger" onClick={deleteItem}>
                                   <i className="fas fa-trash fs-5"></i>
                              </button>
                         </MyTooltip>
                    </div>

                    <div className="fs-8 fw-bold text-gray-600 border-top pt-3">
                         <div className={clsx("badge fw-bolder px-3 py-2", item.remplisTerminadoParCandidat ? "badge-light-success" : "badge-light-warning")}>
                              <i className={clsx("fas me-1", item.remplisTerminadoParCandidat ? "fa-check-circle text-success" : "fa-clock text-warning")}></i>
                              {item.remplisTerminadoParCandidat ? "Complété" : "En attente"}
                         </div>
                    </div>
               </div>
          </div>
     )
}

const DocoListingContext = createContext<ISearchResultsPreviewDocoListingContextPropsModel>({} as ISearchResultsPreviewDocoListingContextPropsModel)
const useDocoListingContext = () => useContext(DocoListingContext)

// --- MAIN COMPONENT ---
interface Props {
     standaloneCandidat?: { id: number; prenomNom: string; email: string } // Permet l'usage hors contexte recherche
     onSuccess?: () => void
}

export const CandidatsRechercheResultsPreviewHelpersDocos = ({ standaloneCandidat, onSuccess }: Props) => {
     const queryClient = useQueryClient()
     const globalContext = useSearchResultsContext()
     const previewContext = useSearchResultsPreviewContext()
     const fileInputRef = useRef<HTMLInputElement>(null)

     // Identification du candidat (Prop > Contexte)
     const currentCandidat = useMemo(() => {
          if (standaloneCandidat) return standaloneCandidat
          return globalContext?.itemInPreview?.selectedItem?.candidat
     }, [standaloneCandidat, globalContext?.itemInPreview?.selectedItem])

     const candidatId = currentCandidat?.id

     const [selectedDocoInPreview, setSelectedDocoInPreview] = useState<{ item: ICandidatDocumentModel; mode: "view" | "edit"; loading: boolean } | null>(null)
     const [docoPartagerParMail, setDocoPartagerParMail] = useState<ICandidatDocumentModel | null>(null)
     const [isGenererDocoEtNotifierCandidatModalShown, setIsGenererDocoEtNotifierCandidatModalShown] = useState<boolean>(false)

     const [isDragOver, setIsDragOver] = useState(false)
     const [isUploading, setIsUploading] = useState(false)

     const docosQuery = useQuery<ICandidatDocumentModel[], AxiosError>(
          [REACT_QUERY_KEY_GET_DOCOS_QUERY, candidatId],
          () => {
               if (!candidatId) return Promise.resolve([])
               return getDocos(candidatId as number)
                    .then(r => r.data)
                    .catch((e: AxiosError) => {
                         toast.error(e.response?.data?.detail, { autoClose: false })
                         throw e
                    })
                    .finally(() => {
                         // Usage de l'optional chaining pour ne pas casser si les contextes sont absents
                         previewContext?.setIsPreviewBodyLoading?.(false)
                         globalContext?.setItemInPreview?.(prev => (prev ? { ...prev, isSelectedItemLoading: false } : null))
                    })
          },
          { enabled: !!candidatId }
     )

     const uploadFile = (file: File) => {
          if (!candidatId) return
          setIsUploading(true)
          uploadDoco(candidatId as number, file)
               .then(response => {
                    const newDoco = response.data
                    queryClient.setQueryData<ICandidatDocumentModel[] | undefined>([REACT_QUERY_KEY_GET_DOCOS_QUERY, candidatId], prev => (prev ? [newDoco, ...prev] : [newDoco]))
                    toast.success("Fichier uploadé avec succès")
                    onSuccess?.() // Trigger success callback
               })
               .catch(e => toast.error("Echec de l'upload: " + (e?.response?.data?.detail || e.message)))
               .finally(() => setIsUploading(false))
     }

     const handleFiles = (files: FileList) => {
          if (files.length > 0) uploadFile(files[0])
     }

     const handleDrop = useCallback(
          (e: React.DragEvent) => {
               e.preventDefault()
               e.stopPropagation()
               setIsDragOver(false)
               if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
          },
          [candidatId]
     )

     // --- MODALS RENDER ---
     const ItemPreviewInModal = useMemo(() => {
          if (!selectedDocoInPreview) return null
          return (
               <MyGoogleDriveFileViewerInModal
                    modalTitle={<span>{selectedDocoInPreview.item.intitule}</span>}
                    googleDriveMimetype={selectedDocoInPreview.item.googleDriveMimeType || CONST_MIME_APPLICATION_GOOGLE_DOC}
                    googleDriveID={selectedDocoInPreview.item.googleDriveID as string}
                    modalSize="xl"
                    handleCloseModal={() => setSelectedDocoInPreview(null)}
               />
          )
     }, [selectedDocoInPreview])

     const PartagerDocoParMailFormInModal = useMemo(() => {
          if (!docoPartagerParMail) return null
          return (
               <MyModal title={<span>Partage par mail</span>} show onCloseCallback={() => setDocoPartagerParMail(null)} size="lg">
                    <CandidatDocoPartagerParMailForm
                         candidatDoco={{ id: docoPartagerParMail.id as number, intitule: docoPartagerParMail.intitule as string }}
                         handleSubmitCallBack={() => setDocoPartagerParMail(null)}
                    />
               </MyModal>
          )
     }, [docoPartagerParMail])

     const GenererDocoEtNotifierCandidatParMailFormInModal = useMemo(() => {
          if (!isGenererDocoEtNotifierCandidatModalShown || !currentCandidat) return null
          return (
               <MyModal title={<span>Générer DoCo & Notifier</span>} show onCloseCallback={() => setIsGenererDocoEtNotifierCandidatModalShown(false)} size="lg">
                    <CandidatGenererDocoEtNotifierCandidatParMailForm
                         candidat={{ id: currentCandidat.id!, prenomNom: currentCandidat.prenomNom!, email: currentCandidat.email! }}
                         handleSubmitCallBack={newDoco => {
                              queryClient.setQueryData<ICandidatDocumentModel[] | undefined>([REACT_QUERY_KEY_GET_DOCOS_QUERY, candidatId], prev =>
                                   prev ? [newDoco, ...prev] : prev
                              )
                              setIsGenererDocoEtNotifierCandidatModalShown(false)
                              onSuccess?.() // Trigger success callback
                         }}
                    />
               </MyModal>
          )
     }, [isGenererDocoEtNotifierCandidatModalShown, currentCandidat, candidatId, onSuccess])

     return (
          <DocoListingContext.Provider value={{ selectedDocoInPreview, setSelectedDocoInPreview, docosQuery, docoPartagerParMail, setDocoPartagerParMail }}>
               <MyLoadingOverlay isActive={isUploading}>
                    <>
                         <div className="preview-height py-8 px-4">
                              <div className="d-flex flex-column align-items-center mb-8">
                                   <div
                                        className={clsx(
                                             "w-100 border border-2 border-dashed rounded p-6 d-flex flex-column align-items-center justify-content-center bg-hover-light-primary transition-all",
                                             isDragOver ? "bg-light-primary border-primary" : "border-gray-300"
                                        )}
                                        onDragOver={e => {
                                             e.preventDefault()
                                             setIsDragOver(true)
                                        }}
                                        onDragLeave={() => setIsDragOver(false)}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ minHeight: "120px", cursor: "pointer" }}
                                   >
                                        <div className="d-flex gap-4" onClick={e => e.stopPropagation()}>
                                             <button className="btn btn-sm btn-primary fw-bold" onClick={() => setIsGenererDocoEtNotifierCandidatModalShown(true)}>
                                                  <i className="fas fa-magic me-2" /> Générer un DoCo Vierge
                                             </button>
                                             <div className="vr opacity-25 mx-2"></div>
                                             <button className="btn btn-sm btn-light-primary fw-bold" onClick={() => fileInputRef.current?.click()}>
                                                  <i className="fas fa-cloud-upload-alt me-2" /> Upload un Fichier
                                             </button>
                                             <input type="file" ref={fileInputRef} className="d-none" onChange={e => e.target.files && handleFiles(e.target.files)} />
                                        </div>
                                        <div className="mt-3 text-gray-400 fs-7">Ou glissez-déposez votre fichier ici directement</div>
                                   </div>
                              </div>

                              <div className="separator separator-dotted border-gray-300 mb-6" />

                              {docosQuery.isLoading ? (
                                   <div className="d-flex justify-content-center py-10">
                                        <div className="spinner-border text-primary"></div>
                                   </div>
                              ) : docosQuery.error ? (
                                   <MyAlert type="danger">{docosQuery.error.response?.data.detail}</MyAlert>
                              ) : (
                                   <div className="row g-4">
                                        {docosQuery.data && docosQuery.data.length > 0 ? (
                                             docosQuery.data.map((item, index) => (
                                                  <div className="col-12 col-md-6" key={item.id || index}>
                                                       <DocoItem item={item} index={index} candidatId={candidatId as number} />
                                                  </div>
                                             ))
                                        ) : (
                                             <div className="text-center py-10 opacity-75">
                                                  <i className="fas fa-folder-open text-warning fs-1 mb-4 d-block"></i>
                                                  <div className="text-gray-600 fs-5 fw-bold">Aucun document disponible</div>
                                             </div>
                                        )}
                                   </div>
                              )}
                         </div>
                         {selectedDocoInPreview && ItemPreviewInModal}
                         {docoPartagerParMail && PartagerDocoParMailFormInModal}
                         {isGenererDocoEtNotifierCandidatModalShown && GenererDocoEtNotifierCandidatParMailFormInModal}
                    </>
               </MyLoadingOverlay>
          </DocoListingContext.Provider>
     )
}
